import { type DragEvent, type FormEvent, type KeyboardEvent, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Upload, Music, Bot, Play, Loader2, ChevronRight,
  CheckCircle2, Circle, Tag
} from "lucide-react";

import {
  advanceStep,
  fetchBackgroundSounds,
  type BackgroundSound,
  uploadBackgroundSound,
  fetchProjectAudio,
  fetchProjectTranscriptions,
  fetchAudioSelection,
  saveAudioSelection,
  fetchProjectProfile,
  type BackgroundSelection
} from "@/api/client";
import { useSessionStore } from "@/hooks/useSessionStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { VoiceSourceUploader } from "@/components/audio-selection/VoiceSourceUploader";
import { VoiceGridPicker } from "@/components/audio-selection/VoiceGridPicker";

const formatDuration = (seconds?: number) => {
  if (seconds == null) return null;
  if (seconds < 1) return `${Math.round(seconds * 1000)} ms`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return mins > 0 ? `${mins}:${secs.toString().padStart(2, "0")}` : `${secs}s`;
};

export function AudioSelectionView() {
  const { sessionId, projectName, setCurrentStep, updateProgress } = useSessionStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [selectedAmbient, setSelectedAmbient] = useState<string | null>(null);
  const [selectedPunctual, setSelectedPunctual] = useState<string[]>([]);
  const [autoBackgrounds, setAutoBackgrounds] = useState(false);
  const [selectedVoices, setSelectedVoices] = useState<string[]>([]);
  const [selectedVoiceId, setSelectedVoiceId] = useState<string | null>(null);
  const [backgroundTitle, setBackgroundTitle] = useState("");
  const [backgroundFile, setBackgroundFile] = useState<File | null>(null);
  const [backgroundStatus, setBackgroundStatus] = useState<string | null>(null);
  const [nextStatus, setNextStatus] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showTranscriptionWait, setShowTranscriptionWait] = useState(false);

  const hiddenBgInput = useRef<HTMLInputElement | null>(null);
  const voicesRef = useRef<string[]>([]);
  // Removed voice preview ref

  const { data: backgroundSounds } = useQuery<BackgroundSound[]>({ queryKey: ["background-sounds"], queryFn: () => fetchBackgroundSounds() });
  const { data: projectAudio } = useQuery({
    queryKey: ["project-audio", projectName],
    queryFn: () => fetchProjectAudio(projectName!),
    enabled: Boolean(projectName)
  });
  const profileQuery = useQuery({
    queryKey: ["project-profile", projectName],
    queryFn: () => fetchProjectProfile(projectName!),
    enabled: Boolean(projectName)
  });
  const selectionQuery = useQuery({
    queryKey: ["audio-selection", sessionId],
    queryFn: () => fetchAudioSelection(sessionId!),
    enabled: Boolean(sessionId)
  });

  const totalAudioFiles = projectAudio?.length ?? 0;

  const transcriptionWaitQuery = useQuery({
    queryKey: ["transcription-wait", projectName],
    queryFn: () => fetchProjectTranscriptions(projectName!),
    enabled: showTranscriptionWait && Boolean(projectName),
    refetchInterval: showTranscriptionWait ? 3000 : false,
  });

  useEffect(() => {
    if (!showTranscriptionWait) return;
    const done = transcriptionWaitQuery.data?.transcriptions.length ?? 0;
    const failed = transcriptionWaitQuery.data?.failedFiles.length ?? 0;
    if (totalAudioFiles > 0 && done + failed >= totalAudioFiles) {
      setShowTranscriptionWait(false);
      updateProgress({ audioReady: true, transcriptionsReviewed: false });
      setCurrentStep("transcription_review");
      navigate("/step/transcription_review");
    }
  }, [transcriptionWaitQuery.data, showTranscriptionWait, totalAudioFiles, navigate, setCurrentStep, updateProgress]);

  const ttsProvider = profileQuery.data?.tts_provider === "qwen" ? "qwen" : "elevenlabs";

  const saveSelection = useMutation({
    mutationFn: (payload: { voices: string[]; backgrounds: BackgroundSelection; auto_backgrounds?: boolean; tts_voice_id?: string | null }) =>
      saveAudioSelection(sessionId!, { project_name: projectName!, ...payload }),
    onSuccess: (data) => {
      selectionQuery.refetch();
      if (data.voices.length > 0) updateProgress({ audioReady: true });
    }
  });

  const persist = (voices: string[], backgrounds: BackgroundSelection, voiceId?: string | null, autoFlag = autoBackgrounds) => {
    saveSelection.mutate({ voices, backgrounds, auto_backgrounds: autoFlag, ...(voiceId ? { tts_voice_id: voiceId } : {}) });
  };

  useEffect(() => {
    if (!selectionQuery.data) return;
    const bg = selectionQuery.data.backgrounds || { ambient: null, punctual: [] };
    setSelectedAmbient(bg.ambient ?? null);
    setSelectedPunctual(bg.punctual ?? []);
    setAutoBackgrounds(Boolean(selectionQuery.data.auto_backgrounds));
    const voices = selectionQuery.data.voices || [];
    setSelectedVoices(voices);
    voicesRef.current = voices;
    setSelectedVoiceId(selectionQuery.data.tts_voice_id ?? null);
  }, [selectionQuery.data]);

  useEffect(() => {
    if (ttsProvider !== "elevenlabs") setSelectedVoiceId(null);
  }, [ttsProvider]);

  useEffect(() => {
    const handler = () => selectionQuery.refetch();
    window.addEventListener("audio-selection-updated", handler);
    return () => window.removeEventListener("audio-selection-updated", handler);
  }, [selectionQuery]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ voice_id: string; voice_label?: string; reason?: string }>).detail;
      if (detail?.voice_id) {
        // Mise à jour visuelle immédiate — select_voice a déjà sauvegardé côté serveur
        setSelectedVoiceId(detail.voice_id);
        // Invalider le cache pour resynchronisation en arrière-plan (sans bloquer l'UI)
        queryClient.invalidateQueries({ queryKey: ["audio-selection", sessionId] });
      }
    };
    window.addEventListener("voice-selected", handler);
    return () => window.removeEventListener("voice-selected", handler);
  }, [sessionId, queryClient]);

  // Removed voice preview cleanups

  if (!sessionId || !projectName) {
    return <p className="text-sm text-muted-foreground">Sélectionnez d'abord un projet.</p>;
  }

  // Upload handled by VoiceSourceUploader

  const handleBackgroundUpload = async (evt: FormEvent) => {
    evt.preventDefault();
    if (!backgroundTitle.trim() || !backgroundFile) { setBackgroundStatus("Titre et fichier requis."); return; }
    setBackgroundStatus("Ajout en cours…");
    await uploadBackgroundSound(backgroundTitle.trim(), backgroundFile);
    setBackgroundStatus("Ajouté !");
    setBackgroundTitle("");
    setBackgroundFile(null);
    queryClient.invalidateQueries({ queryKey: ["background-sounds"] });
  };

  // Voice preview handled by VoiceGridPicker

  const toggleVoice = (track: string) => {
    setSelectedVoices((prev) => {
      if (prev.includes(track)) {
        const next = prev.filter((p) => p !== track);
        voicesRef.current = next;
        persist(next, { ambient: selectedAmbient, punctual: selectedPunctual }, selectedVoiceId);
        updateProgress({ transcriptionsReviewed: false });
        setSelectionError(null);
        return next;
      }
      if (prev.length >= 3) { setSelectionError("Maximum 3 pistes."); return prev; }
      const next = [...prev, track];
      voicesRef.current = next;
      persist(next, { ambient: selectedAmbient, punctual: selectedPunctual }, selectedVoiceId);
      updateProgress({ transcriptionsReviewed: false });
      setSelectionError(null);
      return next;
    });
  };

  const handleAmbientSelect = (path: string | null) => {
    setSelectionError(null);
    setAutoBackgrounds(false);
    const updatedPunctual = path ? selectedPunctual.filter((p) => p !== path) : selectedPunctual;
    setSelectedPunctual(updatedPunctual);
    setSelectedAmbient(path);
    persist(selectedVoices, { ambient: path, punctual: updatedPunctual }, selectedVoiceId, false);
  };

  const handlePunctualToggle = (path: string) => {
    setSelectionError(null);
    setAutoBackgrounds(false);
    setSelectedPunctual((prev) => {
      if (prev.includes(path)) {
        const next = prev.filter((p) => p !== path);
        persist(selectedVoices, { ambient: selectedAmbient, punctual: next }, selectedVoiceId, false);
        return next;
      }
      if (prev.length >= 2) { setSelectionError("Maximum 2 sons ponctuels."); return prev; }
      const next = [...prev, path];
      persist(selectedVoices, { ambient: selectedAmbient === path ? null : selectedAmbient, punctual: next }, selectedVoiceId, false);
      if (selectedAmbient === path) setSelectedAmbient(null);
      return next;
    });
  };

  const handleAutoToggle = (enabled: boolean) => {
    setSelectionError(null);
    setAutoBackgrounds(enabled);
    if (enabled) {
      persist(selectedVoices, { ambient: null, punctual: [] }, selectedVoiceId, true);
    } else {
      persist(selectedVoices, { ambient: selectedAmbient, punctual: selectedPunctual }, selectedVoiceId, false);
    }
  };

  const goNext = async () => {
    const voices = voicesRef.current;
    if (voices.length === 0) { setSelectionError("Sélectionnez au moins une piste vocale."); return; }
    if (ttsProvider === "elevenlabs" && !selectedVoiceId) { setSelectionError("Sélectionnez une voix ElevenLabs."); return; }
    try {
      setNextStatus("Validation des sources…");
      await advanceStep(sessionId, "audio_sources", {
        files: voices,
        backgrounds: { ambient: selectedAmbient, punctual: selectedPunctual },
        auto_backgrounds: autoBackgrounds,
        tts_voice_id: selectedVoiceId ?? undefined
      });
      // Check if transcription is already complete
      const transcriptionResult = await fetchProjectTranscriptions(projectName!);
      const done = transcriptionResult.transcriptions.length;
      const failed = transcriptionResult.failedFiles.length;
      if (totalAudioFiles > 0 && done + failed >= totalAudioFiles) {
        updateProgress({ audioReady: true, transcriptionsReviewed: false });
        setCurrentStep("transcription_review");
        navigate("/step/transcription_review");
      } else {
        setShowTranscriptionWait(true);
      }
    } catch (err) {
      setSelectionError((err as Error).message);
    } finally {
      setNextStatus(null);
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight text-foreground">Sources audio</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Importez vos pistes vocales et configurez les ambiances sonores.
        </p>
      </div>

      {selectionError && (
        <Alert variant="destructive">
          <AlertDescription>{selectionError}</AlertDescription>
        </Alert>
      )}

      <VoiceSourceUploader
        projectName={projectName}
        onUploadSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["project-audio", projectName] });
          queryClient.invalidateQueries({ queryKey: ["audio-selection", sessionId] });
        }}
      />

      {/* Voice track selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Music className="h-4 w-4" />
            Pistes vocales
            {selectedVoices.length > 0 && (
              <Badge variant="secondary">{selectedVoices.length} / 3 sélectionnée{selectedVoices.length > 1 ? "s" : ""}</Badge>
            )}
          </CardTitle>
          <CardDescription>Sélectionnez jusqu'à 3 pistes utilisées pour la narration.</CardDescription>
        </CardHeader>
        <CardContent>
          {projectAudio && projectAudio.length > 0 ? (
            <ul className="flex flex-col gap-1.5">
              {projectAudio.map((track) => {
                const isSelected = selectedVoices.includes(track);
                return (
                  <li key={track}>
                    <label
                      className={cn(
                        "flex items-center gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-all text-sm",
                        isSelected ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                      )}
                    >
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={isSelected}
                        onChange={() => toggleVoice(track)}
                      />
                      <span className={cn("flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors",
                        isSelected ? "border-primary bg-primary text-primary-foreground" : "border-border"
                      )}>
                        {isSelected && <CheckCircle2 className="h-3 w-3" />}
                      </span>
                      <span className="truncate font-medium">{track}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">Aucun fichier importé pour le moment.</p>
          )}
        </CardContent>
      </Card>

      {/* ElevenLabs voice picker */}
      {ttsProvider === "elevenlabs" && (
        <VoiceGridPicker
          selectedVoiceId={selectedVoiceId}
          onSelect={(voiceId) => {
            setSelectionError(null);
            setSelectedVoiceId(voiceId);
            persist(selectedVoices, { ambient: selectedAmbient, punctual: selectedPunctual }, voiceId);
          }}
        />
      )}

      {/* Background ambiance library */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Music className="h-4 w-4" />
            Bibliothèque d'ambiances
          </CardTitle>
          <CardDescription>1 fond continu (optionnel) + jusqu'à 2 sons ponctuels.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="auto-bg" className="flex items-center gap-2">
              <Bot className="h-4 w-4 text-primary" />
              Sélection automatique par l'IA
            </Label>
            <Switch id="auto-bg" checked={autoBackgrounds} onCheckedChange={handleAutoToggle} />
          </div>

          {autoBackgrounds && (
            <div className="flex items-start gap-3 rounded-lg border border-primary/20 bg-info-muted px-4 py-3">
              <Bot className="h-4 w-4 text-info-foreground mt-0.5 shrink-0" />
              <div className="text-sm text-foreground">
                <p className="font-semibold">Sélection automatique activée</p>
                <p className="text-xs mt-0.5">Les ambiances seront choisies automatiquement lors de la génération audio, en fonction du texte du scénario.</p>
              </div>
            </div>
          )}

          {!autoBackgrounds && (
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name="ambient-choice"
                checked={!selectedAmbient}
                onChange={() => handleAmbientSelect(null)}
                className="accent-primary"
              />
              Aucun fond continu
            </label>
          )}

          <div className="flex flex-col gap-2 max-h-80 overflow-y-auto pr-1">
            {backgroundSounds?.map((sound: BackgroundSound) => {
              const isAmbient = selectedAmbient === sound.path;
              const isPunctual = selectedPunctual.includes(sound.path);
              const isSelected = isAmbient || isPunctual;
              return (
                <div
                  key={sound.path}
                  className={cn(
                    "rounded-lg border p-3 flex flex-col gap-2 transition-all",
                    isSelected ? "border-primary bg-primary/5" : "border-border"
                  )}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm flex-1">{sound.name}</span>
                    {sound.category && (
                      <Badge variant="secondary" className="text-xs">{sound.category}</Badge>
                    )}
                    {sound.duration != null && (
                      <span className="text-xs text-muted-foreground">{formatDuration(sound.duration)}</span>
                    )}
                    <audio controls src={sound.preview} className="h-7" />
                  </div>
                  {sound.description && (
                    <p className="text-xs text-muted-foreground">{sound.description}</p>
                  )}
                  {sound.tags && sound.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {sound.tags.map((tag) => (
                        <span key={tag} className="flex items-center gap-0.5 text-xs bg-secondary rounded-full px-2 py-0.5 text-muted-foreground">
                          <Tag className="h-2.5 w-2.5" />{tag}
                        </span>
                      ))}
                    </div>
                  )}
                  {!autoBackgrounds && (
                    <div className="flex gap-4">
                      <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                        <input type="radio" name="ambient-choice" checked={isAmbient} onChange={() => handleAmbientSelect(sound.path)} className="accent-primary" />
                        Fond continu
                      </label>
                      <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                        <input type="checkbox" checked={isPunctual} onChange={() => handlePunctualToggle(sound.path)} className="accent-primary" />
                        Son ponctuel
                      </label>
                    </div>
                  )}
                </div>
              );
            })}
            {!backgroundSounds?.length && (
              <p className="text-sm text-muted-foreground">Aucun son dans la bibliothèque. Ajoutez-en ci-dessous.</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Upload background */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Upload className="h-4 w-4" />Ajouter une ambiance</CardTitle>
          <CardDescription>Contribuez à la bibliothèque partagée.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleBackgroundUpload} className="flex flex-col gap-3">
            <Input
              value={backgroundTitle}
              onChange={(e) => setBackgroundTitle(e.target.value)}
              placeholder="Titre (ex: Quai humide, matin de novembre)"
            />
            <div
              className={cn(
                "flex items-center justify-center rounded-lg border-2 border-dashed px-4 py-6 cursor-pointer transition-colors",
                isDragging ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
              )}
              onDrop={(e: DragEvent<HTMLDivElement>) => {
                e.preventDefault();
                setIsDragging(false);
                const f = e.dataTransfer.files?.[0];
                if (f) { setBackgroundFile(f); setBackgroundStatus(`${f.name}`); }
              }}
              onDragOver={(e: DragEvent<HTMLDivElement>) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onClick={() => hiddenBgInput.current?.click()}
            >
              <div className="text-center">
                <Upload className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  {backgroundFile ? backgroundFile.name : "Glissez ou cliquez pour sélectionner"}
                </p>
              </div>
              <input
                ref={hiddenBgInput}
                type="file"
                accept="audio/*"
                hidden
                onChange={(e) => setBackgroundFile(e.target.files?.[0] ?? null)}
              />
            </div>
            <div className="flex items-center gap-3">
              <Button type="submit" size="sm">Ajouter à la bibliothèque</Button>
              {backgroundStatus && <span className="text-sm text-muted-foreground">{backgroundStatus}</span>}
            </div>
          </form>
        </CardContent>
      </Card>

      <Separator />

      <div className="flex items-center gap-3">
        {nextStatus && (
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {nextStatus}
          </span>
        )}
        <Button onClick={goNext} disabled={!sessionId || Boolean(nextStatus)}>
          Étape suivante
          <ChevronRight className="ml-1 h-4 w-4" />
        </Button>
      </div>

      <Dialog open={showTranscriptionWait}>
        <DialogContent className="max-w-md border border-blue-100 bg-white p-6 shadow-xl rounded-2xl" onInteractOutside={(e) => e.preventDefault()}>
          <style dangerouslySetInnerHTML={{__html: `
            @keyframes soundwave-pulse {
              0%, 100% { transform: scaleY(0.3); }
              50% { transform: scaleY(1); }
            }
            .soundwave-bar {
              transform-origin: bottom;
              animation: soundwave-pulse 1.2s ease-in-out infinite;
            }
          `}} />
          <div className="flex flex-col items-center text-center">
            {/* Animated Audio Soundwave Visualizer */}
            <div className="flex items-end justify-center gap-1.5 h-12 mb-6">
              <span className="w-1.5 h-full bg-blue-500 rounded-full soundwave-bar" style={{ animationDelay: '0.1s' }} />
              <span className="w-1.5 h-full bg-blue-400 rounded-full soundwave-bar" style={{ animationDelay: '0.3s' }} />
              <span className="w-1.5 h-full bg-blue-600 rounded-full soundwave-bar" style={{ animationDelay: '0.5s' }} />
              <span className="w-1.5 h-full bg-blue-500 rounded-full soundwave-bar" style={{ animationDelay: '0.2s' }} />
              <span className="w-1.5 h-full bg-blue-400 rounded-full soundwave-bar" style={{ animationDelay: '0.4s' }} />
            </div>

            <h3 className="text-xl font-bold text-[#0F172B] tracking-tight">
              Transcription en cours...
            </h3>
            
            <p className="text-[#64748B] text-sm font-normal mt-2 max-w-[280px]">
              Vos fichiers audio sont en cours d'analyse pour en extraire les voix et événements historiques.
            </p>

            {/* Status Card */}
            {totalAudioFiles > 0 && (
              <div className="w-full mt-6 px-4 py-3.5 bg-blue-50/50 border border-blue-100/50 rounded-2xl flex flex-col items-center">
                <span className="text-[10px] font-bold text-blue-600 uppercase tracking-widest mb-1.5">
                  Statut du traitement
                </span>
                <span className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" />
                  {transcriptionWaitQuery.data?.transcriptions.length ?? 0} / {totalAudioFiles} fichier{totalAudioFiles > 1 ? "s" : ""} prêt{(transcriptionWaitQuery.data?.transcriptions.length ?? 0) > 1 ? "s" : ""}
                </span>
              </div>
            )}

            <p className="text-xs text-slate-400 font-normal mt-5 leading-normal">
              La page se mettra à jour automatiquement. Ne fermez pas cette fenêtre.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
